import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { SelectInput } from 'components/Inputs/SelectInput/SelectInput';
import { ChangeEvent } from 'react';
import {
  IsoWeekday,
  SummaryMonthlyMode,
  SummaryOccurrence,
} from 'services/api/invoiceNotification/types';

import {
  ALL_DAYS,
  DAYS_OF_MONTH,
  formatWeekday,
  IntlByKey,
  LAST_DAY,
  OCCURRENCES,
  SummaryScheduleFormState,
} from './summarySchedule';
import { Row, Section, SectionLabel } from './SummaryScheduleDialog.style';

interface MonthlyScheduleFieldsProps {
  form: Pick<SummaryScheduleFormState, 'mode' | 'day' | 'occurrence' | 'weekday'>;
  locale: string;
  scheduleIntl: IntlByKey;
  onChange: (changes: Partial<SummaryScheduleFormState>) => void;
}

export const MonthlyScheduleFields = ({
  form,
  locale,
  scheduleIntl,
  onChange,
}: MonthlyScheduleFieldsProps): JSX.Element => {
  const dayOfMonthChoices = [
    ...DAYS_OF_MONTH.map((day) => ({ label: String(day), value: day })),
    { label: scheduleIntl('last-day'), value: LAST_DAY },
  ];
  const occurrenceChoices = OCCURRENCES.map((occurrence) => ({
    label: scheduleIntl(`occurrence.${occurrence}`),
    value: occurrence,
  }));
  const weekdayChoices = ALL_DAYS.map((day) => ({
    label: formatWeekday(day, locale, 'long'),
    value: day,
  }));

  return (
    <Section>
      <SectionLabel>{scheduleIntl('on')}</SectionLabel>
      <RadioGroup
        value={form.mode}
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          onChange({ mode: event.target.value as SummaryMonthlyMode })
        }
      >
        <Row>
          <FormControlLabel
            value="dayOfMonth"
            control={<Radio size="small" />}
            label={scheduleIntl('day-of-month')}
          />
          <SelectInput
            id="summary-day-of-month"
            width={140}
            preserveSpacingForHelperText={false}
            disabled={form.mode !== 'dayOfMonth'}
            value={form.day}
            choices={dayOfMonthChoices}
            onChange={(event) =>
              onChange({
                day: event.target.value === LAST_DAY ? LAST_DAY : Number(event.target.value),
              })
            }
          />
        </Row>
        <Row>
          <FormControlLabel
            value="weekdayOfMonth"
            control={<Radio size="small" />}
            label={scheduleIntl('weekday-of-month')}
          />
          <SelectInput
            id="summary-occurrence"
            width={120}
            preserveSpacingForHelperText={false}
            disabled={form.mode !== 'weekdayOfMonth'}
            value={form.occurrence}
            choices={occurrenceChoices}
            onChange={(event) =>
              onChange({ occurrence: Number(event.target.value) as SummaryOccurrence })
            }
          />
          <SelectInput
            id="summary-weekday"
            width={150}
            preserveSpacingForHelperText={false}
            disabled={form.mode !== 'weekdayOfMonth'}
            value={form.weekday}
            choices={weekdayChoices}
            onChange={(event) => onChange({ weekday: Number(event.target.value) as IsoWeekday })}
          />
        </Row>
      </RadioGroup>
    </Section>
  );
};
